import { createStyles, makeUiStyles, Theme } from '../styles/styles';
import { fontDisplay, fontText, hexToRgbA, StylesIndexInjections } from 'common-react';
import { focusStyle } from '../styles/mixins';
import { getChartTimeRangesPanelStyles, getHighChartChartWrapperStyles, getHighChartLegendInteractionStyles } from '../utils/highChartTooltipStyles';

export interface FundBigChartPropsStyle {
  isActive?: boolean;
  color?: string;
  checked?: boolean;
  embeddedInFundPopup?: boolean;
  isDarkTheme?: boolean;
}

export default makeUiStyles((theme: Theme) => createStyles({
  root: {
    marginTop: -4,
    [theme.breakpoints.down('xs')]: {
      marginTop: 20
    }
  },

  tabButton: {
    padding: '8px 16px',
    position: 'relative',
    zIndex: 1,
    cursor: 'pointer',
    font: fontText('normal', theme.fontSize.s, 18),
    border: `1px solid ${theme.color.greys[15]}`,
    backgroundColor: ({ isActive }: FundBigChartPropsStyle) => {
      const colors = {
        dark: isActive ? theme.color.greys[10] : theme.color.greys[0],
        light: isActive ? theme.color.greys[80] : theme.color.greys[0],
      };
      return theme.variables.isDarkTheme ? colors.dark : colors.light;
    },
    color: ({ isActive }: FundBigChartPropsStyle) => isActive ? theme.color.constants.greys[0] : theme.color.greys[80],

    '&:focus-visible': {
      ...focusStyle(theme),
      zIndex: 2
    },

    '&:first-child': {
      borderTopLeftRadius: 4,
      borderBottomLeftRadius: 4,
    },

    '&:last-child': {
      borderTopRightRadius: 4,
      borderBottomRightRadius: 4,
    },
  },

  panel: {
    display: 'flex',
    flexWrap: 'wrap',
    [theme.breakpoints.down('xs')]: {
      flexDirection: 'column'
    },
    marginBottom: 10,
  },
  dropdownLabel: {
    marginTop: 1,
    marginBottom: 10,
    display: 'inline-block',
    font: fontText('normal', theme.fontSize.s, 16),
    textTransform: 'uppercase',
    color: theme.color.greys[80]
  },
  dropdownBox: {
    display: 'flex',
    flexDirection: 'column',
    marginRight: 20,
    [theme.breakpoints.down('xs')]: {
      margin: '0 0 22px 0'
    },
  },
  categoryUnitBox: {
    minWidth: 360,
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    [theme.breakpoints.down('xs')]: {
      width: '100%',
      minWidth: 0,
    },
  },
  categoryUnitBoxDisabled: {
    '& $categoryUnitValue': {
      opacity: 0.4,
      pointerEvents: 'none',
      userSelect: 'none',
    },
  },
  categoryUnitValue: {
    font: fontText('normal', theme.fontSize.s, 18),
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    maxWidth: 360,
    padding: 16,
    border: `1px solid ${theme.color.greys[40]}`,
    borderRadius: 8,
    backgroundColor: theme.color.greys[0],
    color: theme.variables.isDarkTheme ? theme.color.greys[20] : theme.color.greys[50],
    [theme.breakpoints.down('xs')]: {
      maxWidth: 'initial',
    },
  },
  pricingBox: {
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
    '& > div:not(:last-child)': {
      marginRight: 40,
      [theme.breakpoints.down('sm')]: {
        marginRight: 24,
      }
    },

    [theme.breakpoints.down('sm')]: {
      marginTop: 8,
      justifyContent: 'flex-start',
      flexWrap: 'wrap',
    }
  },
  pricingBoxRow: {
    display: 'flex',
    flexWrap: 'wrap',
    '& > div': {
      flexShrink: 0,
    },
    '& $pricingHeaderBox:first-child': {
      marginRight: 24,
    },
    [theme.breakpoints.down('sm')]: {
      flexWrap: 'nowrap',
      justifyContent: 'space-between',
    },
  },
  pricingHeaderBox: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    [theme.breakpoints.down('sm')]: {
      '&:first-child': {
        marginLeft: 0,
      },
      '&:not(:first-child)': {
        marginLeft: 5,
      },
      minWidth: 117,
      marginTop: 16,
    },
  },
  pricingHeaderROIBox: {
    gap: 2,
  },

  panelFooter: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',

    [theme.breakpoints.down('sm')]: {
      flexDirection: ({ embeddedInFundPopup }: FundBigChartPropsStyle) => embeddedInFundPopup ? 'column' : 'row',
      alignItems: ({ embeddedInFundPopup }: FundBigChartPropsStyle) => embeddedInFundPopup ? 'flex-start' : 'center',
    },
  },
  buttonsBox: {
    [theme.breakpoints.down('md')]: {
      display: 'flex',
    },
  },
  info: {
    font: fontText('normal', theme.fontSize.base, 24),
    color: theme.color.greys[50],

    [theme.breakpoints.down('sm')]: {
      marginTop: 24,
    },

    [theme.breakpoints.down('xs')]: {
      font: fontText('normal', theme.fontSize.s, 20),
    },
  },
  infoDark: {
    color: theme.color.greys[80]
  },

  legend: {
    marginTop: 20,
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'flex-start',

    [theme.breakpoints.down('xs')]: {
      flexDirection: 'column'
    },

    '& > label': {
      marginBottom: 8
    },

    '& > label:not(:last-child)': {
      marginRight: 12,
      [theme.breakpoints.down('xs')]: {
        marginRight: 0,
      },
    }
  },

  checkboxLabel: {
    cursor: 'pointer',
    display: 'block',
    padding: '6px 20px',
    font: fontText('normal', theme.fontSize.base, 20),
    color: 'rgba(0, 0, 0, 0.5)',
    backgroundColor: ({ color, checked }: FundBigChartPropsStyle) => color && checked ? hexToRgbA(color, 0.2) : theme.color.greys[10],
    borderRadius: 100,

    [theme.breakpoints.down('xs')]: {
      font: fontText('normal', theme.fontSize.s, 16),
    },

    '& > input': {
      display: 'none'
    }
  },

  chartWrapper: {
    minHeight: 400,
    [theme.breakpoints.down('sm')]: {
      ...getHighChartChartWrapperStyles(theme, true),
    },
    [theme.breakpoints.up('sm')]: {
      ...getHighChartChartWrapperStyles(theme, false),
    },
    ...getHighChartLegendInteractionStyles(theme),
  },
  alertBox: {
    marginBottom: 24,
    marginTop: 24
  },
  timeRangesPanel: {
    ...getChartTimeRangesPanelStyles(theme),
  },
  firstRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 24,
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column-reverse',
      alignItems: 'flex-start',
      marginBottom: 16,
    },
  },
  secondRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: 24,
    [theme.breakpoints.down('sm')]: {
      marginBottom: 24,
      marginTop: 24,
    },
  },
  categoryUnitLabel: {
    font: fontDisplay('normal', theme.fontSize.base, 24),
    '& button': {
      position: 'relative',
      top: 2,
    },
  },
  unitCategoryDropdown: {
    marginTop: 4,
    '& .MuiSelect-root': {
      paddingTop: 11,
      paddingBottom: 11,
      color: theme.color.greys[80],
    }
  },
  timeRangesPanelBottom: {
    marginBottom: 0,
  }
}), { name: 'FundBigChart', index: StylesIndexInjections.UI_MOLECULES });
